import { ClientFeatures, ResellerFeatures } from './types';

export const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

export const VOICE_PROVIDERS = {
	'11labs': '11Labs Voice',
	cartesia: 'Cartesia Voice',
	openai: 'OpenAI Voice',
	deepgram: 'Deepgram Voice',
	rime: 'Rime Voice',
	transferredCall: 'Transfer Call',
} as const;

export const BUSINESS_TYPES = [
	'Healthcare',
	'Legal',
	'Real Estate',
	'Restaurant',
	'Retail',
	'Technology',
	'Consulting',
	'Other',
] as const;

// Default reseller features
export const defaultResellerFeatures: ResellerFeatures = {
	voiceCredits: 0,
	smsCredits: 0,
	overageVoiceCharges: {
		displayText: 'Voice Provider Pricing',
		tooltip: 'Pricing for different voice providers',
		charges: {
			'11labs': 0.12,
			cartesia: 0.12,
			openai: 0.12,
			deepgram: 0.12,
			rime: 0.12,
			transferredCall: 0.03,
		},
	},
};

// Default client features with the exact API format
export const defaultClientFeatures: ClientFeatures = {
	advancedCapabilities: {
		multilingualSupport: {
			displayText: 'Non-English Language Support',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Multilingual supports all major languages',
		},
		setMaxRecipientMinutes: {
			displayText: 'Set Max Receptionist Minutes',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Set limits on the maximum amount of time your receptionist can be active',
		},
		timeControls: {
			displayText: 'Time Controls',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Choose specific times your receptionist will passthrough calls to a person',
		},
		zapierIntegration: {
			displayText: 'Zapier Integration',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Connect call log data to other apps',
		},
		teamAccess: {
			displayText: 'Team Access',
			tooltip: 'Manage team member access to your account',
			viewAccessMembers: {
				gatedValues: [{ effectiveAfter: 0, value: -1 }],
			},
			editAccessMembers: {
				gatedValues: [{ effectiveAfter: 0, value: -1 }],
			},
		},
	},
	AnalyticsAndReporting: {
		analyticsTab: {
			displayText: 'Analytics Tab',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Advanced analytics dashboard to understand calls',
		},
		logs: {
			displayText: 'Your Call, Text and Voicemail logs',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'Your Call, Text and Voicemail logs',
		},
		maxNotificationRecipients: {
			displayText: 'Max Notifications Recipients',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip:
				'Automatically send detailed call transcripts via text or email after your receptionist answers a new call',
			value: -1,
		},
		postCallNotifications: {
			displayText: 'Post Call Notifications',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip:
				'Intelligently route notifications to the right people depending on what happened during the call',
		},
		workflowNotifications: {
			displayText: 'Workflow Notifications',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip:
				'Receive advanced notifications when specific workflows are triggered during the call.',
		},
	},
	communicationAndCallHandling: {
		minutesIncluded: {
			displayText: 'Minutes Included',
			tooltip:
				'Typically 250 minutes is enough for most SMBs. After 250 minutes, we charge you a minutely fee for overages depending on the voice you choose hat is typically neglible compared to your monthly fee unless you are a high volume user.',
			value: 300,
		},
		premiumAiModels: {
			displayText: 'Premium AI Models (GPT-4, Claude, Grok)',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Always upgrading AI models',
		},
		premiumVoices: {
			displayText: '50+ Premium Voices',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Access to state-of-the-art, human-like voices',
		},
		smsCreditsIncluded: {
			displayText: 'SMS Credits Included',
			gatedValues: [{ effectiveAfter: 0, value: 83 }],
			tooltip: 'SMS credits included',
			value: 83,
		},
		overageVoiceCharges: {
			displayText: 'Voice Provider Pricing',
			tooltip: 'Pricing for different voice providers',
			charges: {
				'11labs': 0.12,
				cartesia: 0.12,
				openai: 0.12,
				deepgram: 0.12,
				rime: 0.12,
				transferredCall: 0.03,
			},
		},
		stateOfArtLatency: {
			displayText: 'State of the Art Latency',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Our AI receptioinst is the fastest on the market',
		},
		unlimitedParallelCalls: {
			displayText: 'Unlimited Parallel Calls',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Your AI receptionist can handle unlimited calls at the same time',
		},
	},
	knowledgeAndCRM: {
		advancedCRM: {
			displayText: 'Advanced CRM',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Track and manage your leads without manual intervention.',
		},
		knowledgeBaseLengthPages: {
			displayText: 'Knowledge Base Length',
			gatedValues: [{ effectiveAfter: 0, value: 20 }],
			tooltip: 'Tell your AI about your business so that it can answer any question',
		},
	},
	phoneNumbersAndForwarding: {
		keepYourPhoneNumber: {
			displayText: 'Keep Your Phone Number',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip:
				'You can forward calls from your existing business line to the numebr we give you, so your customers will call your original number',
		},
		pickYourAreaCode: {
			displayText: 'Pick your Area Code',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'Choose an area code for your AI receptionist',
		},
		recieveAStandaloneNumber: {
			displayText: 'Receive a Standalone Phone Number',
			gatedValues: [{ effectiveAfter: 0, value: true }],
			tooltip: 'You can receive a phone number directly from us and use it as your own',
		},
	},
	supportAndSLA: {
		supportCalls: {
			displayText: 'Support Calls',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'Our team will always hop on a call with you to help',
		},
		supportSLAWithInHours: {
			displayText: 'Support SLA',
			gatedValues: [{ effectiveAfter: 0, value: 48 }],
			tooltip: 'Email responses',
		},
	},
	webhooks: {
		customWebhooks: {
			displayText: 'Something to display',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'Custom webhooks',
		},
	},
	workflows: {
		apiWorkflows: {
			displayText: 'API Workflows',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'Call the API endpoint you configured when your spoecified scenario happens',
		},
		callTransferWorkflows: {
			displayText: 'Call Transferring Workflows',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip:
				'You can program your receptionist to transfer calls intelligently based on what the caller says. For example, you can program your receptionist to transfer the call to the billing department anytime the caller asks for a refund or receipt.',
		},
		intakeFormWorkflows: {
			displayText: 'Intake Form Workflows',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip:
				'You can program your receptionist to ask the caller specific questions either at (1) the beginning of the call or (2) during a call when a certain scenario occurs. The answers to these questions are stored separately.',
		},
		overThePhoneBookWorkflows: {
			displayText: 'Over-the-Phone Booking Workflows',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'Integration with Google Calendar',
		},
		postCallMessage: {
			displayText: 'Post Call Message',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip: 'post call message',
		},
		textWorkflows: {
			displayText: 'Texting Workflows',
			gatedValues: [{ effectiveAfter: 0, value: -1 }],
			tooltip:
				'You can program your AI receptionist to send custom text messages during a conversation based on what the caller says. For example, if the caller asks to schedule an appointment, the receptionist can text the caller a link to your Calendly page.',
		},
	},
};
